import classnames from 'classnames'
import { Trans, useTranslation } from 'react-i18next'
import { requiredIf } from '../../../../utility/prop-types-helper'
import { useSelector } from 'react-redux'
import { displayWithCurrency } from '../../../../utility/currency'
import { numbersToPercentage } from '../../../../utility/numbersHelper'

import classes from './StatsPanel.module.scss'
import { ReactComponent as InfoCircleIcon } from '../../../../assets/images/common/icons/info-circle-icon.svg'
import { ReactComponent as DollarIcon } from '../../../../assets/images/common/icons/dollar-icon.svg'
import { ReactComponent as BlocksIcon } from '../../../../assets/images/common/icons/blocks-icon.svg'
import PropTypes from 'prop-types'
import Loader from '../../../common/loader/Loader'

const BusinessSummaryWidget = ({ isLoading, businessSummary, packages }) => {
  const { t } = useTranslation()
  const currency = useSelector(state => state.user.current_user.communities[0].currency)

  const withPackages = packages?.length > 0

  if (isLoading) {
    return (
      <section className={classnames('d-flex align-items-center justify-content-center', classes.infoSection)}>
        <Loader>
          {t('programs.manage.businessSummary.loading')}
        </Loader>
      </section>
    )
  }

  return (
    <section className={classnames('p-3 d-flex flex-column', classes.infoSection)}>
      <div className='mb-3 d-flex justify-content-between'>
        <h3 className={classes.title}>{t('programs.manage.businessSummary.title')}</h3>
        <InfoCircleIcon role='button' className={classes.icon} />
      </div>
      <div className='d-flex mb-4'>
        {withPackages && (
          <DollarIcon className='flex-shrink-0 mr-2' />
        )}
        <div className='d-flex flex-column'>
          <span className={classes.subtitle}>{t('programs.manage.businessSummary.revenueGenerated')}</span>
          {withPackages ? (
            <span className={classes.totalRevenue}>
              {displayWithCurrency(businessSummary?.totalRevenue, currency, true)}
            </span>
          ) : (
            <span className={classnames('mt-1', classes.orangeHighlight)}>{t('programs.manage.businessSummary.noPackagesAdded')}</span>
          )}
        </div>
      </div>
      {withPackages ? (
        <>
          <div className={classnames('mb-2', classes.statsItem)}>
            <span>
              <BlocksIcon className='mr-2' />
              {t('programs.manage.businessSummary.packageBreakdown')}
            </span>
          </div>
          <div className='mb-3'>
            {packages?.map((p, index) => (
              <div key={index} className={classnames('mb-2 d-flex justify-content-between', classes.statsItem)}>
                <span>{p.name}</span>
                <div className='d-flex align-items-center'>
                  <span className={classes.orangeHighlight}>{p.enrollmentsCount}</span>
                  <span
                    className={classnames('ml-1', classes.statsItem, classes.smallerFontSize)}>({displayWithCurrency(p.price, currency, true)})</span>
                </div>
              </div>
            ))}
          </div>
          <div className={classnames('pt-3', classes.topSeparator)}>
            <div className={classnames('mb-3 d-flex justify-content-between', classes.statsItem)}>
              <span>{t('programs.manage.businessSummary.averageRevenuePerEnrollment')}</span>
              <span>{displayWithCurrency(businessSummary?.averageEnrollmentRevenue, currency)}</span>
            </div>
            <div className={classnames('mb-3 d-flex justify-content-between', classes.statsItem)}>
              <span>{t('programs.manage.businessSummary.golfersEnrolled')}</span>
              {businessSummary?.golferLimit !== null ? (
                <span>{businessSummary?.enrollmentsSize}/{businessSummary?.golferLimit}</span>
              ) : (
                <span>{businessSummary?.enrollmentsSize}</span>
              )}
            </div>
            {businessSummary?.golferLimit !== null && (
              <>
                <div className={classnames('mb-3 d-flex justify-content-between', classes.statsItem)}>
                  <span>{t('programs.manage.businessSummary.capacity')}</span>
                  <span>{numbersToPercentage(businessSummary?.enrollmentsSize, businessSummary?.golferLimit, true)}</span>
                </div>
                <div className={classnames('mb-3 d-flex justify-content-between', classes.statsItem)}>
                  <span>{t('programs.manage.businessSummary.potentialRevenue')}</span>
                  <span>{displayWithCurrency(businessSummary?.potentialRevenue, currency, true)}</span>
                </div>
              </>
            )}
          </div>
        </>
      ) : (
        <>
          <div className='mb-3 d-flex flex-column align-items-center'>
            <DollarIcon className={classnames('mb-3', classes.placeholderIcon)} />
            <h5 className={classnames('mb-3', classes.placeholderTitle)}>
              {t('programs.manage.businessSummary.addPricingToUnlock')}
            </h5>
            <p className={classnames('mb-0', classes.placeholderText)}>
              <Trans
                i18nKey='programs.manage.businessSummary.addPricingDescription'
                components={{
                  anchor: (
                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                    <a
                      href='https://intercom.help/op-36/en/articles/10670862-how-to-add-pricing-packages'
                      target='_blank'
                      className={classes.link}
                    />
                  ),
                }}
              />
            </p>
          </div>
          {businessSummary?.golferLimit !== null && (
            <div className={classnames('pt-3 d-flex justify-content-between', classes.topSeparator, classes.statsItem)}>
              <span>{t('programs.manage.businessSummary.golfersEnrolled')}</span>
              <span>{businessSummary?.enrollmentsSize}/{businessSummary?.golferLimit}</span>
            </div>
          )}
        </>
      )}
    </section>
  )
}

BusinessSummaryWidget.propTypes = {
  isLoading: PropTypes.bool,
  businessSummary: requiredIf(PropTypes.object, ({ isLoading }) => !isLoading),
  packages: requiredIf(PropTypes.array, ({ isLoading }) => !isLoading),
}

export default BusinessSummaryWidget
